import { createSelector } from '@reduxjs/toolkit'
import api from '~/store/api'
import { selectProject } from '~/store/app/selectors'
import { selectProjectionMap } from '~/store/outputMapping/selectors'

const NONE = []

export const selectMapEditing = s => (selectProjectionMap(s) ? (s.maps?.editing ?? null) : null)

export const selectMapDirty = createSelector(
	s => s.maps?.editing,
	e => !!e && JSON.stringify(e.draft) !== e.saved
)

export const selectMaps = s => api.endpoints.getMaps.select(undefined)(s)?.data ?? NONE

/** Map `id` is known unreadable: the list has loaded without it (a template's map, someone else's project). */
export const selectMapUnreadable = (s, id) => {
	const q = api.endpoints.getMaps.select(undefined)(s)
	return !!id && !!q?.isSuccess && !q.data?.some(m => m.id === id)
}

/** This window's pick, else the project's, else the oldest map (the `Default` every user is given); null until the list loads. */
export const selectActiveMapId = s => {
	const maps = selectMaps(s)
	const id = s.maps?.active_id ?? selectProject(s)?.settings?.map_id
	if (id && maps.some(m => m.id === id)) return id
	return maps.reduce((a, m) => (!a || Date.parse(m.createdAt) < Date.parse(a.createdAt) ? m : a), null)?.id ?? null
}

/** What the outputs show: the editor's draft (the editor always holds the active map), else the map the runner sent, else the saved active map; null for none. */
export const selectShownMap = s => {
	const e = s.maps?.editing
	if (e) return e.draft
	if (s.maps?.remote) return s.maps.remote
	return selectMaps(s).find(m => m.id === selectActiveMapId(s))?.data ?? null
}

/** The version of map `id` its linked stage shapes follow: the editor's draft while it edits that map, else the saved map; undefined when the map is not readable. */
export const selectShapeMapData = (s, id) => {
	const e = s.maps?.editing
	if (e?.id === id) return e.draft
	return selectMaps(s).find(m => m.id === id)?.data
}

/** Show slices grid: `{ slice }`, the selected slice or the selected surface's (null for none); null when off. */
export const selectSlicesGrid = createSelector(
	s => selectProjectionMap(s) && !!s.outputMapping?.slices_grid,
	s => s.maps?.editing?.selected,
	s => s.maps?.editing?.draft,
	(on, selected, draft) => {
		if (!on) return null
		if (selected?.kind === 'slice') return { slice: selected.id }
		return { slice: (selected?.kind === 'surface' && draft?.surfaces[selected.id]?.slice) || null }
	}
)

const selectEditorGuides = createSelector(
	s => s.maps?.editing?.tab,
	s => s.maps?.editing?.tool,
	s => s.maps?.editing?.selected,
	s => s.maps?.editing?.point,
	s => s.maps?.editing?.selection_ids,
	selectSlicesGrid,
	(tab, tool, selected, point, ids, slicesGrid) => ({
		tab,
		tool,
		selected: selected ?? null,
		point: point ?? null,
		selection_ids: ids ?? [],
		slices_grid: slicesGrid,
	})
)

/** The editor's selection every output draws over the shown map (`drawGuides`): this window's editor with Show on output, else what the runner sent; null for none. */
export const selectShownGuides = s => {
	if (s.maps?.editing) return s.outputMapping?.on_output ? selectEditorGuides(s) : null
	return s.maps?.remote_guides ?? null
}

/** The editor's own view under Show grid: 'full' under the Output tab's corner-pin tool, else 'masked'; 'off' without it. */
export const selectEditorCard = s => {
	if (!selectProjectionMap(s) || !s.outputMapping?.grid) return 'off'
	const e = s.maps?.editing
	return e?.tab === 'output' && e.tool === 'pin' ? 'full' : 'masked'
}

/** The outputs: the editor's card with Show on output, else what the runner sent. */
export const selectOutputCard = s => {
	if (s.maps?.editing) return s.outputMapping?.on_output ? selectEditorCard(s) : 'off'
	return s.maps?.remote_card ?? 'off'
}
